import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useNavigationGroups, NavItemDef, isNavPathActive } from "./Sidebar";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import logoLight from "@/assets/logo-light.svg";
import logoDark from "@/assets/logo-dark.svg";

function MobileNavItem({ item }: { item: NavItemDef }) {
  const location = useLocation();
  const isActive = item.path
    ? isNavPathActive(location.pathname, item.path)
    : false;

  const content = (
    <div
      className={cn(
        "flex items-center gap-2.5 w-full px-2.5 py-[7px] rounded-md text-sm transition-colors",
        isActive
          ? "bg-brand-soft text-brand-hover"
          : "text-ink-2 hover:bg-panel-2 hover:text-ink",
      )}
    >
      <item.icon
        className={cn(
          "h-[15px] w-[15px] flex-shrink-0",
          isActive ? "text-brand-hover" : "text-muted",
        )}
      />
      <span>{item.label}</span>
    </div>
  );

  if (!item.path) return <div>{content}</div>;

  return <Link to={item.path}>{content}</Link>;
}

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}

export function MobileMenu({ open, onClose }: MobileMenuProps) {
  const { t } = useTranslation("common");
  const navGroups = useNavigationGroups();

  return (
    <Sheet open={open} onOpenChange={onClose}>
      {/* Flex column so the nav list gets whatever height is left over. A fixed
          `h-[calc(100vh-<header>)]` on the list would overflow the panel on mobile
          browsers, where 100vh exceeds the visible viewport, and the last nav items
          would be unreachable. */}
      <SheetContent side="left" className="w-[280px] p-0 flex flex-col">
        <SheetHeader className="shrink-0 border-b border-hairline px-6 py-4">
          <SheetTitle className="flex items-center gap-3">
            <img
              src={logoDark}
              alt="WiServices"
              className="h-6 block dark:block hidden"
            />
            <img
              src={logoLight}
              alt="WiServices"
              className="h-6 block dark:hidden"
            />
            <span className="text-sm font-mono text-muted tracking-wide">
              Console
            </span>
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="min-h-0 flex-1 px-3 py-2">
          <nav className="flex flex-col gap-0.5" onClick={onClose}>
            {navGroups.map((group) => (
              <div key={group.titleKey} className="mb-3">
                <div className="px-2.5 py-2 text-[10.5px] font-medium tracking-[0.12em] uppercase text-muted-2">
                  {t(group.titleKey)}
                </div>
                {group.items.map((item) => (
                  <MobileNavItem key={item.path} item={item} />
                ))}
              </div>
            ))}
          </nav>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
